import { useCallback, useMemo } from "react";
import { AnyTask, Point } from "tm-shared/types";

const isSearchMatch = (point: Point, search: string) => {
    const lowerSearch = search.toLowerCase();
    return (
        point.market?.contragent?.name?.toLowerCase().includes(lowerSearch) ||
        point.market.building?.address?.name
            ?.toLowerCase()
            .includes(lowerSearch)
    );
};

const collator = new Intl.Collator("uk", { sensitivity: "base" });

export const sortPoints = (points: Point[], sortOption?: string): Point[] => {
    switch (sortOption) {
        case "fop-asc":
            return [...points].sort((a, b) =>
                collator.compare(
                    a.market.contragent?.name,
                    b.market.contragent?.name,
                ),
            );
        case "fop-desc":
            return [...points].sort((a, b) =>
                collator.compare(
                    b.market.contragent?.name,
                    a.market.contragent?.name,
                ),
            );
        case "address-asc":
            return [...points].sort((a, b) =>
                collator.compare(
                    a.market.building.address?.name,
                    b.market.building.address?.name,
                ),
            );
        case "address-desc":
            return [...points].sort((a, b) =>
                collator.compare(
                    b.market.building.address?.name,
                    a.market.building.address?.name,
                ),
            );
        case "route":
        default:
            return points;
    }
};

interface UseFilteredPointsArgs {
    points: Record<string, Point>;
    tasks: Record<string, Record<string, AnyTask>>;
    pointExtraIds: string[];
    searchTerm: string;
    selectedSortOption: { value: string } | null;
}

export const useFilteredPoints = ({
    points,
    tasks,
    pointExtraIds,
    searchTerm,
    selectedSortOption,
}: UseFilteredPointsArgs) => {
    const isCompleted = useCallback(
        (pointId: string) => {
            return Object.values(tasks[pointId] ?? {}).every(
                (task) => task?.completed,
            );
        },
        [tasks],
    );

    const isNotCompleted = useCallback(
        (pointId: string) => {
            return Object.values(tasks[pointId] ?? {}).some(
                (task) => !task?.completed,
            );
        },
        [tasks],
    );

    const filteredPoints = useMemo(() => {
        const list = Object.values(points).filter((point) => {
            if (pointExtraIds?.includes(point.id)) return false;
            if (point.unplanned || isCompleted(point.id)) return false;
            return true;
        });

        const searched = searchTerm
            ? list.filter((point) => isSearchMatch(point, searchTerm))
            : list;

        return sortPoints(searched, selectedSortOption?.value);
    }, [
        points,
        searchTerm,
        selectedSortOption?.value,
        pointExtraIds,
        isCompleted,
    ]);

    const filteredExtraPoints = useMemo(() => {
        if (!pointExtraIds?.length) return [];

        const list = pointExtraIds
            .map((id) => points[id])
            .filter((point): point is Point => !!point)
            .filter((point) => isNotCompleted(point.id));

        const searched = searchTerm
            ? list.filter((point) => isSearchMatch(point, searchTerm))
            : list;

        return sortPoints(searched, selectedSortOption?.value);
    }, [
        pointExtraIds,
        searchTerm,
        selectedSortOption?.value,
        points,
        isNotCompleted,
    ]);

    const filteredCompletedPoints = useMemo(() => {
        const list = Object.values(points).filter((point) => {
            if (point.unplanned && !tasks[point.id]) return false;
            return isCompleted(point.id);
        });

        const searched = searchTerm
            ? list.filter((point) => isSearchMatch(point, searchTerm))
            : list;

        return sortPoints(searched, selectedSortOption?.value);
    }, [points, searchTerm, selectedSortOption?.value, tasks, isCompleted]);

    return { filteredPoints, filteredExtraPoints, filteredCompletedPoints };
};
